// Tail-round timer policy (the F6 fix) — the SINGLE source for the server's
// degrade ceilings AND the client's lost-frame backstops, so the
// client-above-server invariant can never drift back into the F6 regression.
//
// The post-green tail turn (implementation plan + size estimate) fans out one
// explorer sub-agent per acceptance criterion; measured ~360s on a 6-criteria
// ticket over a ~200k-LoC monorepo. The old ceiling was a fixed
// 90s × (1 + 2 extensions) = 270s, chosen when the tail was plan-only — so the
// server degraded `plan_timeout` while the turn was still productively running
// and the real result landed ~86s after the degrade. The fix: re-arm the degrade
// while the sandbox turn is genuinely in flight (activeTurnDepth > 0), bounded
// by a WALL-CLOCK maximum rather than a fire-count, so it never cuts off a
// productive turn yet still degrades the instant the turn goes idle or wedges
// past the bound.

export const TAIL_PLAN_POLL_MS = 90_000;
export const TAIL_SIZING_POLL_MS = 150_000;
export const BREAKDOWN_POLL_MS = 120_000;

/** Wall-clock ceiling for the plan+size tail turn (one explorer sub-agent per
 *  AC; measured ~360s @6 criteria → 480s leaves ~120s headroom for a wider
 *  partition — sub-agents run in parallel, so more criteria is sublinear). The
 *  server re-arms the degrade while the turn is in flight up to this bound; the
 *  bound only catches a wedged busy-counter, never a productive turn. */
export const TAIL_TURN_WALL_CLOCK_MAX_MS = 480_000;

/** Breakdown proposal/adjust turn (partition + deny-repair over the already
 *  computed sizing map — lighter than plan+size). Preserves today's 360s
 *  effective ceiling; the finding reported no breakdown timeout. */
export const BREAKDOWN_WALL_CLOCK_MAX_MS = 360_000;

/** Breakdown turn that must ALSO rebuild the sizing map first (the re-size
 *  lane: a verified-green card whose estimate a criteria edit invalidated).
 *  That turn is the plan+size fan-out AND the partition in one reply, so the
 *  360s above — explicitly sized for "partition over the already computed
 *  sizing map" — would degrade it while it is still productively running, which
 *  is the exact F6 shape. Budgeted as the tail ceiling plus one breakdown poll:
 *  the sizing half dominates and the partition rides the tail of the same turn. */
export const BREAKDOWN_RESIZE_WALL_CLOCK_MAX_MS =
  TAIL_TURN_WALL_CLOCK_MAX_MS + BREAKDOWN_POLL_MS; // 600s

/** The client backstop sits ABOVE the server ceiling. The `*_unavailable`
 *  degrade is transient — not persisted, not re-emitted on reconnect (unlike
 *  `*_ready`, which self-heals) — and the client wipes its timeline on
 *  reconnect, so a degrade frame lost across a reconnect window would otherwise
 *  strand the loading spinner forever. The backstop rescues that double-fault;
 *  being above the ceiling, a DELIVERED server decision always wins first. */
export const TAIL_CLIENT_BACKSTOP_BUFFER_MS = 60_000;
export const PLAN_CLIENT_BACKSTOP_MS =
  TAIL_TURN_WALL_CLOCK_MAX_MS + TAIL_CLIENT_BACKSTOP_BUFFER_MS; // 540s
export const BREAKDOWN_CLIENT_BACKSTOP_MS =
  BREAKDOWN_WALL_CLOCK_MAX_MS + TAIL_CLIENT_BACKSTOP_BUFFER_MS; // 420s
/** The client cannot see which lane the server took, so its ONE backstop has to
 *  sit above the LONGER of the two ceilings — otherwise a re-size turn would be
 *  cut off client-side at 420s while the server was still legitimately waiting,
 *  which is the client-above-server invariant read backwards. */
export const BREAKDOWN_RESIZE_CLIENT_BACKSTOP_MS =
  BREAKDOWN_RESIZE_WALL_CLOCK_MAX_MS + TAIL_CLIENT_BACKSTOP_BUFFER_MS; // 660s

export interface TailTimerConfig {
  readonly pollMs: number;
  readonly wallClockMaxMs: number;
}

export type TailTimerDecision =
  | { readonly action: "extend"; readonly delayMs: number }
  | { readonly action: "degrade" };

/**
 * Decide whether a firing tail-degrade timer degrades now or re-arms one more
 * poll. Pure — the caller owns setTimeout, the session markers, and the clock.
 * Degrades the instant the sandbox turn is idle (activeTurnDepth === 0) or once
 * the wall-clock max is reached; otherwise extends by one poll, clamped so it
 * never overshoots the wall-clock bound.
 */
export const decideTailTimer = (p: {
  readonly elapsedMs: number;
  readonly activeTurnDepth: number;
  readonly config: TailTimerConfig;
}): TailTimerDecision => {
  const { elapsedMs, activeTurnDepth, config } = p;
  if (activeTurnDepth > 0 && elapsedMs < config.wallClockMaxMs) {
    const remaining = config.wallClockMaxMs - elapsedMs;
    return {
      action: "extend",
      delayMs: Math.max(1, Math.min(config.pollMs, remaining)),
    };
  }
  return { action: "degrade" };
};
